import { convertDiscordImageUrl } from '../utils';
import { BASEJUMP_BI_CONTRACT_ADDRESS } from '../constants';

const IS_PRODUCTION = (process.env.REACT_APP_IS_PRODUCTION || 'false').toLowerCase() === 'true'

export default class AssetModel {
  constructor(alchemyNftData, isMock = true) {
    if (isMock) {
      this.nftData = alchemyNftData;
      this.equipped = !!this.nftData.equipped;
      this.nftData.isReal = false;
      this.nftData.attributes = [];
    } else {
      this.nftData = {
        basejumpItemId: alchemyNftData.itemId,
        contractAddress: alchemyNftData.contractAddress,
        id: alchemyNftData.tokenId,
        attributes: alchemyNftData.rawMetadata.attributes.map(a => ({ name: a.trait_type, value: a.value})),
        image: alchemyNftData.rawMetadata.image,
        name: alchemyNftData.rawMetadata.name,
        description: alchemyNftData.rawMetadata.description,
        glb: alchemyNftData.rawMetadata.glb,
        type: alchemyNftData.rawMetadata.type,
        subtype: alchemyNftData.rawMetadata.subtype,
        effects: alchemyNftData.rawMetadata.effects,
        henchie_glb: alchemyNftData.rawMetadata.henchie_glb,
        price: alchemyNftData.rawMetadata.price,
        priceXp: alchemyNftData.rawMetadata.price_xp,
        pricePoints: alchemyNftData.rawMetadata.price_points,
        skinName: alchemyNftData.rawMetadata.skin_name,
        stats: alchemyNftData.rawMetadata.stats,
        rarity: alchemyNftData.rawMetadata.rarity,
        locked: alchemyNftData.rawMetadata.locked,
        isReal: true,
      }
      this.equipped = alchemyNftData.equipped;
      this.slot = alchemyNftData.slot;
    }
  }

  getPrice() {
    const { price, priceXp, pricePoints } = this.nftData;
    return { price, priceXp, pricePoints };
  }

  isLocked() {
    return !!this.nftData.locked;
  }

  isReal() {
    return !!this.nftData.isReal;
  }

  getItemId() {
    return this.nftData.basejumpItemId;
  }

  getSkinName() {
    return this.nftData.skinName;
  }

  getEffects() {
    return this.nftData?.effects || [];
  }

  getStats() {
    return {
      healthPoints: this.nftData.stats.health_points,
      attackPoints: this.nftData.stats.attack_points,
      defencePoints: this.nftData.stats.defence_points,
      healthMultiplier: this.nftData.stats.health_multiplier,
      attackMultiplier: this.nftData.stats.attack_multiplier,
      defenceMultiplier: this.nftData.stats.defence_multiplier,
      criticalChancePoints: this.nftData.stats.critical_chance_points,
      speedPoints: this.nftData.stats.speed_points,
      criticalChanceMultiplier: this.nftData.stats.critical_chance_multiplier,
      speedMultiplier: this.nftData.stats.speed_multiplier
    };
  }

  static parseAttributes(basejumpData) {
    const attributes = [
      { trait_type: 'Skin', value: basejumpData.item.skin_name },
      { trait_type: 'Rarity', value: basejumpData.item.rarity }
    ];

    if (basejumpData.item.stats.health_points !== 0) {
      attributes.push({ trait_type: 'Health Points', value: `+${basejumpData.item.stats.health_points}` });
    }

    if (basejumpData.item.stats.attack_points !== 0) {
      attributes.push({ trait_type: 'Attack Points', value: `+${basejumpData.item.stats.attack_points }` });
    }
    
    if (basejumpData.item.stats.defence_points !== 0) {
      attributes.push(
        { trait_type: 'Defense Points', value: `+${basejumpData.item.stats.defence_points}` }
      );
    }

    if (basejumpData.item.stats.speed_points !== 0) {
      attributes.push(
        { trait_type: 'Speed Points', value: `+${basejumpData.item.stats.speed_points}` }
      );
    }
    
    if (basejumpData.item.stats.critical_chance_points !== 0) {
      attributes.push(
        { trait_type: 'Critical Chance', value: `+${basejumpData.item.stats.critical_chance_points}%` }
      );
    }

    if (basejumpData.item.stats.health_multiplier !== 1.0) {
      attributes.push(
        { trait_type: 'Health Multiplier', value: `x${Math.floor((basejumpData.item.stats.health_multiplier - 1) * 100)}%` }
      );
    }

    if (basejumpData.item.stats.attack_multiplier !== 1.0) {
      attributes.push(
        { trait_type: 'Attack Multiplier', value: `x${Math.floor((basejumpData.item.stats.attack_multiplier - 1) * 100)}%` }
      );
    }
    
    if (basejumpData.item.stats.defence_multiplier !== 1.0) {
      attributes.push(
        { trait_type: 'Defense Multiplier', value: `x${Math.floor((basejumpData.item.stats.defence_multiplier - 1) * 100)}%` }
      );
    }

    if (basejumpData.item.stats.speed_multiplier !== 1.0) {
      attributes.push(
        { trait_type: 'Speed Multiplier', value: `x${Math.floor((basejumpData.item.stats.speed_multiplier - 1) * 100)}%` }
      );
    }

    if (basejumpData.item.stats.critical_chance_multiplier !== 1.0) {
      attributes.push(
        { trait_type: 'Crit Multiplier', value: `x${Math.floor((basejumpData.item.stats.critical_chance_multiplier - 1) * 100)}%` }
      );
    }
    return attributes;
  }

  static fromBasejumpEquipment(basejumpData, convertForDiscord = false) {
    const alchemyNftData = {
      itemId: basejumpData.id,
      tokenId: basejumpData.item.token_id,
      equipped: basejumpData.equipped,
      slot: basejumpData.slot,
      contractAddress: basejumpData.item.contract_address,
      rawMetadata: {
        effects: basejumpData.item.effects,
        rarity: basejumpData.item.rarity,
        locked: !!basejumpData.item.locked,
        attributes: AssetModel.parseAttributes(basejumpData),
        stats: basejumpData.item.stats,
        image: convertDiscordImageUrl(basejumpData.item.image_url, convertForDiscord),
        name: basejumpData.item.name,
        description: basejumpData.item.description,
        glb: basejumpData.item.glb_url,
        type: basejumpData.item.item_type,
        subtype: basejumpData.item.item_sub_type,
        henchie_glb: basejumpData.item.henchie_glb_url,
        price: basejumpData.item.price,
        price_xp: basejumpData.item.price_xp,
        price_points: basejumpData.item.price_points,
        skin_name: basejumpData.item.skin_name
      }
    };
    return new AssetModel(alchemyNftData, false);
  }
  static fromAlchemyNft(alchemyNftData, isMock = true) {
    return new AssetModel(alchemyNftData, isMock);
  }

  getContractAddress() {
    return this.nftData.contractAddress;
  }

  getRarity() {
    return this.nftData.rarity;
  }

  getName() {
    return this.nftData.name;
  }

  getId() {
    return this.nftData.id;
  }

  getDescription() {
    return this.nftData.description;
  }

  getImgSource() {
    return this.nftData.image;
  }

  isEquipped () {
    return this.equipped;
  }

  getType() {
    return this.nftData.type;
  }

  getSubType() {
    return this.nftData.subtype;
  }

  getGlbSource() {
    return this.nftData.glb;
  }

  getOpenSeaLink() {
    return IS_PRODUCTION
      ? `https://opensea.io/assets/base/${BASEJUMP_BI_CONTRACT_ADDRESS}/${this.getId()}`
      : `https://testnets.opensea.io/assets/base-sepolia/${BASEJUMP_BI_CONTRACT_ADDRESS}/${this.getId()}`;
  }

  getTraits() {
    return this.nftData.attributes;
  }
}
